import firebase from "firebase/app";

export const journalCollectionKey = "journals";
export const journalReflectionCollectionKey = "reflections";
export const journalCommentCollectionKey = "comments";
export const journalAiAnalysisCollectionKey = "analyses"; // AIによるジャーナルの分析（先生単位で作る）

export type Journal = {
  ref: firebase.firestore.DocumentReference;
  data: JournalData;
};

export type JournalLabel = {
  name: string;
  description: string;
};

export type TutorAccessType = "private" | "public" | "publicToOwner"; //前から順に、自分だけ閲覧可, 全員閲覧可, 自分とオーナーだけ閲覧可

export type JournalReflectionType = "text" | "graph";

export type JournalReflectionItem = {
  title: string;
  type: JournalReflectionType;
  uid: string;
  createdAt: number;
};

export type JournalData = {
  title: string;
  description: string;
  label: JournalLabel;
  journalReflectionItems: JournalReflectionItem[];
  tutorAccess: TutorAccessType;
  availableToAllStudents: boolean;
  studentIds: string[]; //availableToAllStudentsがfalseの時のみ評価される
  isDeleted: boolean;
  isCompleted: boolean;
  isDraft: boolean;
  publishedAt: number | null; //isDraftが解除されたunix(速攻公開の場合はcreatedAtと同値、後で公開する場合はそれまでnullの想定)
  publishedBy: string; //操作をしたtutorのuid
  createdBy: string; //操作をしたtutorのuid
  updatedBy?: string[]; //操作をしたtutorのuid
  deletedBy?: string; //操作をしたtutorのuid
  createdAt: number;
  updatedAt?: number[];
  deletedAt?: number;
  prompt?: string; //AIからコメントを取得する時に使われるプロンプト
  includeChatDataInPrompt?: boolean;
};

export function convertToJournal(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): Journal {
  const returnData: JournalData = {
    title: data.title,
    description: data.description,
    label: data.label,
    journalReflectionItems: data.journalReflectionItems,
    tutorAccess: data.tutorAccess,
    availableToAllStudents: data.availableToAllStudents,
    studentIds: data.studentIds,
    isDeleted: data.isDeleted,
    isCompleted: data.isCompleted,
    createdBy: data.createdBy,
    createdAt: data.createdAt,
    isDraft: data.isDraft,
    publishedAt: data.publishedAt,
    publishedBy: data.publishedBy
  };

  if (data.updatedBy) {
    returnData.updatedBy = data.updatedBy;
  }

  if (data.deletedBy) {
    returnData.deletedBy = data.deletedBy;
  }

  if (data.updatedAt) {
    returnData.updatedAt = data.updatedAt;
  }

  if (data.deletedAt) {
    returnData.deletedAt = data.deletedAt;
  }

  if (data.prompt) {
    returnData.prompt = data.prompt;
  }

  if (data.includeChatDataInPrompt) {
    returnData.includeChatDataInPrompt = data.includeChatDataInPrompt;
  }

  return {
    ref,
    data: returnData
  };
}

export type JournalReflection = {
  ref: firebase.firestore.DocumentReference;
  data: JournalReflectionData;
};

export type JournalReflectionContent = {
  content: string | number;
  createdAt: number;
  targetReflectionItemId: string; //先生が指定するjournal内の振り返り項目のuid
  updatedAt?: number;
};

export type JournalReflectionSubmitterDetail = {
  studentId: string;
  classroomId: string;
  schoolId: string;
};

export type JournalReflectionData = {
  reflections: JournalReflectionContent[];
  conversationRef: firebase.firestore.DocumentReference | null; //AIとの会話と紐づけ(紐づけなければnull)
  submitterDetail: JournalReflectionSubmitterDetail;
  date: string; //2024-08-24のような形式を想定
  createdAt: number;
  isParentJournalDeleted: boolean;
  updatedAt?: number;
  relatedReflectionIds?: string[]; //この振り返りを登録した時点で参照していた他の振り返りのID配列
  aiComment?: string; //この振り返り登録時に生成されたAIコメント
  aiCommentCreatedAt?: number; //AIコメントが生成されたunix time
  aiCommentPrompt?: string; //AIコメント生成時に使われたプロンプト(デバッグ/再利用用)
};

export function convertToJournalReflection(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): JournalReflection {
  const returnData: JournalReflectionData = {
    reflections: data.reflections,
    conversationRef: data.conversationRef,
    submitterDetail: data.submitterDetail,
    date: data.date,
    createdAt: data.createdAt,
    isParentJournalDeleted: data.isParentJournalDeleted
  };

  if (data.updatedAt) {
    returnData.updatedAt = data.updatedAt;
  }

  if (data.relatedReflectionIds) {
    returnData.relatedReflectionIds = data.relatedReflectionIds;
  }

  if (data.aiComment) {
    returnData.aiComment = data.aiComment;
  }

  if (data.aiCommentCreatedAt) {
    returnData.aiCommentCreatedAt = data.aiCommentCreatedAt;
  }

  if (data.aiCommentPrompt) {
    returnData.aiCommentPrompt = data.aiCommentPrompt;
  }

  return {
    ref,
    data: returnData
  };
}

export type JournalComment = {
  ref: firebase.firestore.DocumentReference;
  data: JournalCommentData;
};

export type JournalCommentData = {
  comment: string;
  relatedReflectionIds: string[];
  createdAt: number;
  isJournalDeleted: boolean;
};

export function convertToJournalComment(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): JournalComment {
  const returnData: JournalCommentData = {
    comment: data.comment,
    relatedReflectionIds: data.relatedReflectionIds,
    createdAt: data.createdAt,
    isJournalDeleted: data.isJournalDeleted
  };

  return {
    ref,
    data: returnData
  };
}

export type JournalAiAnalysis = {
  ref: firebase.firestore.DocumentReference;
  data: JournalAiAnalysisData;
};

export type JournalAiAnalysisData = {
  analysis: string;
  createdAt: number;
};

export function convertToJournalAiAnalysis(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): JournalAiAnalysis {
  const returnData: JournalAiAnalysisData = {
    analysis: data.analysis,
    createdAt: data.createdAt
  };

  return {
    ref,
    data: returnData
  };
}
